// The parts a public ledger message is sent in, ported from ns-lambda-ts splitMessage (_core/public-ledger/messages.ts) so
// the browser knows the text each part's schedule must carry before it signs it (#24). The server schedules part i with
// splitMessage(content.text)[i]; the two must split alike, so change neither alone.

// The most one ledger message holds, in UTF-8 bytes (the network's limit for a single, unchunked message)
export const MAX_MESSAGE_BYTES = 1024;

const utf8Length = (text: string) => new TextEncoder().encode(text).length;

/**
 * A message as the parts it is sent in: each at most maxBytes of UTF-8, never splitting a character. A message that fits is
 * one part.
 */
export function splitMessage(text: string, maxBytes: number = MAX_MESSAGE_BYTES): string[] {
  if (!Number.isInteger(maxBytes) || maxBytes < 4) throw new Error(`Invalid part size: ${maxBytes}`);
  if (utf8Length(text) <= maxBytes) return [text];
  const parts: string[] = [];
  let current = '';
  let currentBytes = 0;
  // for..of walks code points, so a surrogate pair is never cut
  for (const char of text) {
    const bytes = utf8Length(char);
    if (currentBytes + bytes > maxBytes) {
      parts.push(current);
      current = '';
      currentBytes = 0;
    }
    current += char;
    currentBytes += bytes;
  }
  if (current) parts.push(current);
  return parts;
}

/**
 * The text of one part, as the server scheduled it: partIndex is null on the first part of a message stored before parts
 * were numbered, which is part 0. null when the message has no such part.
 */
export function messagePartText(text: string, partIndex: number | null | undefined): string | null {
  return splitMessage(text)[partIndex ?? 0] ?? null;
}
